import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import PreviewerDownloadIcon from "@/assets/summary/ic_previewer_download.svg?react";
import PreviewerPrevIcon from "@/assets/summary/ic_previewer_prev.svg?react";
import PreviewerNextIcon from "@/assets/summary/ic_previewer_next.svg?react";
import PreviewerCloseIcon from "@/assets/summary/ic_previewer_close.svg?react";

import { createPortal } from "react-dom";
import { debounce } from "@common/utils";
import { toast, Id } from "react-toastify";
import summaryCommonStore from "@/store/summaryCommon.store";

let toastId: Id;
let messageTimer: NodeJS.Timeout;
const MessageTimeout = 3 * 1000;

interface ImagePreviewerProps { }
export interface ImagePreviewerRef {
  handlePreview: (images: string[], defaultIndex?: number) => void;
}

const ImagePreviewer = forwardRef<ImagePreviewerRef, ImagePreviewerProps>(
  (_props, ref) => {
    const [images, setImages] = useState<string[]>([]);
    const [visible, setVisible] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const lastToastType = useRef<'first' | 'last'>()
    const activeIndexRef = useRef(activeIndex);
    const imagesLenRef = useRef(images.length);
    const [isActive, setIsActive] = useState(false);
    activeIndexRef.current = activeIndex;
    imagesLenRef.current = images.length;

    const closeToast = () => {
      toastId && toast.dismiss(toastId);
      toastId = '';
    }

    const setMessageTimer = (content: string, toastType: 'first' | 'last') => {
      if (!toastId || lastToastType.current !== toastType) {
        toastId && toast.dismiss(toastId);
        toastId = toast.dark(content, {
          autoClose: false
        });
      }

      lastToastType.current = toastType;
      messageTimer && clearTimeout(messageTimer);
      messageTimer = setTimeout(() => {
        closeToast();
      }, MessageTimeout);
    };

    useEffect(() => {
      const handleKeyDown = (event: { key: string; preventDefault: () => void; stopPropagation: () => void; }) => {
        if (!isActive) return; // 如果组件不是激活状态，不处理按键事件
        event.preventDefault();
        event.stopPropagation();
        if (event.key === 'ArrowLeft') {
          handlePrev();
        } else if (event.key === 'ArrowRight') {
          handleNext();
        }
        return false;
      };

      if (visible) {
        summaryCommonStore.getState().setImagePreviewerVisible(true);
        window.addEventListener('keydown', handleKeyDown);
      } else {
        summaryCommonStore.getState().setImagePreviewerVisible(false);
      }

      return () => {
        summaryCommonStore.getState().setImagePreviewerVisible(false);
        window.removeEventListener('keydown', handleKeyDown);
      };
    }, [visible, isActive]);

    const handlePrev = debounce(() => {
      if (activeIndexRef.current > 0) {
        closeToast();
        setActiveIndex((prev) => prev - 1);
        activeIndexRef.current--;
      } else {
        setMessageTimer("当前已是第一张图片", 'first');
      }
    }, 100);

    const handleNext = debounce(() => {
      if (activeIndexRef.current < imagesLenRef.current - 1) {
        closeToast();
        setActiveIndex((prev) => prev + 1);
        activeIndexRef.current++;
      } else {
        setMessageTimer("当前已是最后一张图", 'last');
      }
    }, 100);

    const handleDownload = debounce(() => {
      const targetUrl = images[activeIndex];
      const link = document.createElement('a');
      link.href = targetUrl;
      link.download = 'downloaded_image.jpg';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }, 100);

    const handleClose = () => {
      setVisible(false);
    };

    const handlePreview = (images: string[], defaultIndex?: number) => {
      setImages(images);
      setActiveIndex(defaultIndex || 0);
      setVisible(true);
    };

    useImperativeHandle(ref, () => ({
      handlePreview,
      handleClose,
    }));

    const targetUrl = images[activeIndex];
    const root = document.getElementById("root");

    useEffect(() => {
      return () => {
        closeToast();
      }
    }, [visible])


    return visible && root
      ? createPortal(
        <div
          className={`fixed top-0 left-0 w-full h-full`}
          onClick={(e) => {
            e.stopPropagation();
          }}
          onMouseEnter={() => setIsActive(true)} // 鼠标进入时激活
          onMouseLeave={() => setIsActive(false)} // 鼠标离开时取消激活
        >
          <div className={`w-full h-full bg-black bg-opacity-70`}>
            <div className="absolute left-0 bg-#000 right-0 top-15.5 bottom-15.5">
              <img
                src={targetUrl}
                className="w-full h-full object-contain select-none"
              />
            </div>
            <span className="absolute w-fit top-11.25 left-0 right-0 m-auto text-#fff/30 font-500 text-5 lh-5.5 select-none">
              <span className="color-#fff">{activeIndex + 1}</span>/
              {images.length}
            </span>
            <PreviewerDownloadIcon
              className="absolute top-8 left-6 cursor-pointer no_drag"
              onClick={handleDownload}
            />
            <PreviewerCloseIcon
              className="absolute top-8 right-6 cursor-pointer no_drag"
              onClick={handleClose}
            />
            <PreviewerPrevIcon
              className="absolute top-0 bottom-0 m-auto left-8 cursor-pointer"
              onClick={handlePrev}
            />
            <PreviewerNextIcon
              className="absolute top-0 bottom-0 m-auto right-8 cursor-pointer"
              onClick={handleNext}
            />
          </div>
        </div>,
        root
      )
      : null;
  }
);

export default ImagePreviewer;
